import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useFeatures } from '../lib/features';
import { getCalendarIcon } from '../lib/calendarIcons';
import NewsdeskSentInfo, { SentPill, SentControl } from '../components/NewsdeskSentInfo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  List as ListIcon,
  AlertCircle,
  Filter,
  ExternalLink,
} from 'lucide-react';

const DEFAULT_COLOR = '#3B82F6';

// Resolve the icon for a booking. Priority: enriched product_type_calendar_icon
// (from backend digital/schedule endpoint) → productTypes lookup → default.
const iconFor = (b, productIconMap = {}) => {
  const key = b?.product_type_calendar_icon || productIconMap[b?.product_type] || null;
  return getCalendarIcon(key);
};

// ─── Date helpers ────────────────────────────────────────────────────────────
const isoDate = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + n);
  return nd;
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfWeek = (d) => {
  const nd = new Date(d);
  nd.setDate(nd.getDate() - nd.getDay());
  return nd;
};
const monthLabel = (d) =>
  d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
const dayLabel = (d) =>
  d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
const shortDay = (d) => d.toLocaleDateString(undefined, { weekday: 'short' });

// Is this booking a multi-day range (bar) or single-day (chip)?
const isRangeBooking = (b) => {
  if (b.product_type_is_single_day) return false;
  const s = b.digital_start_date;
  const e = b.digital_end_date || b.digital_start_date;
  return !!(s && e && e > s);
};

// Sort range bookings: start-date asc, longer campaigns first, then product, then advertiser
const sortRangeBookings = (a, b) => {
  const sa = a.digital_start_date || '';
  const sb = b.digital_start_date || '';
  if (sa !== sb) return sa.localeCompare(sb);
  const ea = a.digital_end_date || a.digital_start_date || '';
  const eb = b.digital_end_date || b.digital_start_date || '';
  // Longer campaign first → later end date first
  if (ea !== eb) return eb.localeCompare(ea);
  const pa = a.product_type || '';
  const pb = b.product_type || '';
  if (pa !== pb) return pa.localeCompare(pb);
  return (a.advertiser_name || '').localeCompare(b.advertiser_name || '');
};

// Sort single-day items: product_type, advertiser, created_at
const sortSingleDayBookings = (a, b) => {
  const pa = a.product_type || '';
  const pb = b.product_type || '';
  if (pa !== pb) return pa.localeCompare(pb);
  const na = a.advertiser_name || '';
  const nb = b.advertiser_name || '';
  if (na !== nb) return na.localeCompare(nb);
  return (a.created_at || '').localeCompare(b.created_at || '');
};

/**
 * Given the ISO dates of a week (Sun..Sat) and a list of range bookings,
 * assign each booking a lane (0, 1, 2…) such that no two bookings in the
 * same lane overlap on any day. Returns an array of segments:
 *   { booking, lane, colStart (0-6), colEnd (0-6, inclusive), startsInRow, endsInRow }
 * Sorted by lane then colStart so rendering order is stable.
 */
const layoutRangeBookingsForWeek = (weekIsoDates, rangeBookings) => {
  const weekStart = weekIsoDates[0];
  const weekEnd = weekIsoDates[weekIsoDates.length - 1];
  // Filter to those overlapping the week
  const inWeek = rangeBookings.filter((b) => {
    const s = b.digital_start_date;
    const e = b.digital_end_date || b.digital_start_date;
    return s && e && s <= weekEnd && e >= weekStart;
  }).slice().sort(sortRangeBookings);

  const lanes = []; // lanes[laneIndex] = latest colEnd occupied
  const segments = [];
  inWeek.forEach((b) => {
    const s = b.digital_start_date;
    const e = b.digital_end_date || b.digital_start_date;
    const colStart = Math.max(0, weekIsoDates.indexOf(s));
    let colEnd = weekIsoDates.indexOf(e);
    if (colEnd === -1) colEnd = 6; // ends after this week
    if (colStart === -1) return;   // shouldn't happen due to inWeek filter
    // Find first lane where lanes[i] < colStart (no overlap)
    let lane = lanes.findIndex((last) => last < colStart);
    if (lane === -1) { lanes.push(colEnd); lane = lanes.length - 1; }
    else { lanes[lane] = colEnd; }
    segments.push({
      booking: b,
      lane,
      colStart,
      colEnd,
      startsInRow: s >= weekStart,
      endsInRow: e <= weekEnd,
    });
  });
  segments.sort((a, b) => a.lane - b.lane || a.colStart - b.colStart);
  return { segments, laneCount: lanes.length };
};

// ─── Sort helpers ───────────────────────────────────────────────────────────

const DigitalPage = () => {
  const { user } = useAuth();
  const canEdit = user?.role !== 'sales_rep';

  const today = new Date();
  const [view, setView] = useState('calendar'); // 'calendar' | 'list' | 'unscheduled'
  const [cursor, setCursor] = useState(startOfMonth(today));
  const [calendarScope, setCalendarScope] = useState('month'); // 'month' | 'week' | 'day'
  const [scheduled, setScheduled] = useState([]);
  const [unscheduled, setUnscheduled] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [editions, setEditions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filters, setFilters] = useState({
    productType: 'all',
    advertiser: 'all',
    salesRep: 'all',
    status: 'all',
    mode: 'all', // 'single' | 'range' | 'all'
  });

  // Selected booking for edit modal
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

  // ── Data loaders ──────────────────────────────────────────────────────────
  const scopeWindow = useMemo(() => {
    if (calendarScope === 'day') {
      const s = isoDate(cursor);
      return { start: s, end: s };
    }
    if (calendarScope === 'week') {
      const s = startOfWeek(cursor);
      return { start: isoDate(s), end: isoDate(addDays(s, 6)) };
    }
    // month: expand to whole visible grid (6 weeks)
    const first = startOfMonth(cursor);
    const gridStart = startOfWeek(first);
    return { start: isoDate(gridStart), end: isoDate(addDays(gridStart, 41)) };
  }, [cursor, calendarScope]);

  const loadSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const [sched, unsched, pts, advs, reps, eds, s] = await Promise.all([
        api.get(`/digital/schedule?start=${scopeWindow.start}&end=${scopeWindow.end}`),
        api.get('/digital/unscheduled'),
        api.get('/product-types?category=website'),
        api.get('/advertisers'),
        api.get('/sales-reps'),
        api.get('/editions'),
        api.get('/settings'),
      ]);
      setScheduled(sched || []);
      setUnscheduled(unsched || []);
      setProductTypes(pts || []);
      setAdvertisers(advs || []);
      setSalesReps(reps || []);
      setEditions(eds || []);
      setSettings(s || null);
    } catch (err) {
      console.error('Failed to load digital data:', err);
    } finally {
      setLoading(false);
    }
  }, [scopeWindow.start, scopeWindow.end]);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  // ── Product type index ────────────────────────────────────────────────────
  const productColor = useMemo(() => {
    const map = {};
    productTypes.forEach(pt => { map[pt.name] = pt.calendar_color || DEFAULT_COLOR; });
    return map;
  }, [productTypes]);
  const productIconMap = useMemo(() => {
    const map = {};
    productTypes.forEach(pt => { if (pt.calendar_icon) map[pt.name] = pt.calendar_icon; });
    return map;
  }, [productTypes]);

  // ── Filtered bookings ─────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return scheduled.filter(b => {
      if (filters.productType !== 'all' && b.product_type !== filters.productType) return false;
      if (filters.advertiser !== 'all' && b.advertiser_id !== filters.advertiser) return false;
      if (filters.salesRep !== 'all' && b.sales_rep_id !== filters.salesRep) return false;
      if (filters.status !== 'all' && b.status !== filters.status) return false;
      if (filters.mode === 'single' && !b.product_type_is_single_day) return false;
      if (filters.mode === 'range' && b.product_type_is_single_day) return false;
      return true;
    });
  }, [scheduled, filters]);

  const filteredUnscheduled = useMemo(() => {
    return unscheduled.filter(b => {
      if (filters.productType !== 'all' && b.product_type !== filters.productType) return false;
      if (filters.advertiser !== 'all' && b.advertiser_id !== filters.advertiser) return false;
      if (filters.salesRep !== 'all' && b.sales_rep_id !== filters.salesRep) return false;
      return true;
    });
  }, [unscheduled, filters]);

  // ── Bookings grouped by date for the calendar ─────────────────────────────
  // Single-day chips are indexed per-day. Range bookings are rendered as bars
  // laid out per-week by layoutRangeBookingsForWeek(), so we keep them in a
  // flat list here.
  const rangeBookings = useMemo(
    () => filtered.filter(isRangeBooking).sort(sortRangeBookings),
    [filtered],
  );
  const singleByDate = useMemo(() => {
    const map = {};
    filtered.filter(b => !isRangeBooking(b)).forEach((b) => {
      const d = b.digital_start_date;
      if (!d) return;
      (map[d] = map[d] || []).push(b);
    });
    Object.keys(map).forEach(k => { map[k].sort(sortSingleDayBookings); });
    return map;
  }, [filtered]);

  // ── Today panel: running / scheduled / upcoming ───────────────────────────
  const todayISO = isoDate(today);
  const runningToday = useMemo(
    () => scheduled.filter(b => {
      const s = b.digital_start_date, e = b.digital_end_date || b.digital_start_date;
      return s && e && s <= todayISO && e >= todayISO;
    }),
    [scheduled, todayISO]
  );
  const upcomingWeek = useMemo(() => {
    const in7 = isoDate(addDays(today, 7));
    return scheduled.filter(b => {
      const s = b.digital_start_date;
      return s && s > todayISO && s <= in7;
    }).sort((a, b) => (a.digital_start_date || '').localeCompare(b.digital_start_date || ''));
  }, [scheduled, todayISO]);
  const endingSoon = useMemo(() => {
    const in3 = isoDate(addDays(today, 3));
    return scheduled.filter(b => {
      const e = b.digital_end_date || b.digital_start_date;
      return e && e >= todayISO && e <= in3 && !b.product_type_is_single_day;
    }).sort((a, b) => (a.digital_end_date || '').localeCompare(b.digital_end_date || ''));
  }, [scheduled, todayISO]);

  // ── Navigation ────────────────────────────────────────────────────────────
  const goPrev = () => {
    if (calendarScope === 'day') setCursor(addDays(cursor, -1));
    else if (calendarScope === 'week') setCursor(addDays(cursor, -7));
    else setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  };
  const goNext = () => {
    if (calendarScope === 'day') setCursor(addDays(cursor, 1));
    else if (calendarScope === 'week') setCursor(addDays(cursor, 7));
    else setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  };
  const goToday = () => {
    if (calendarScope === 'month') setCursor(startOfMonth(today));
    else setCursor(new Date());
  };

  const rangeLabel = calendarScope === 'day' ? dayLabel(cursor)
                    : calendarScope === 'week' ? `${dayLabel(startOfWeek(cursor))} — ${dayLabel(addDays(startOfWeek(cursor), 6))}`
                    : monthLabel(cursor);

  const openBookingModal = (b) => {
    setSelectedBooking(b);
    setScheduleModalOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="digital-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#F8FAFC]">Digital</h1>
          <p className="text-sm text-[#94A3B8]">
            Calendar &amp; schedule for all website-category ad bookings.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={view === 'calendar' ? 'default' : 'outline'}
            onClick={() => setView('calendar')}
            className={view === 'calendar' ? 'bg-[#3B82F6] hover:bg-[#2563EB] text-white' : 'border-[#1E293B] text-[#94A3B8]'}
            data-testid="digital-view-calendar"
          >
            <CalendarIcon className="w-4 h-4 mr-2" /> Calendar
          </Button>
          <Button
            variant={view === 'list' ? 'default' : 'outline'}
            onClick={() => setView('list')}
            className={view === 'list' ? 'bg-[#3B82F6] hover:bg-[#2563EB] text-white' : 'border-[#1E293B] text-[#94A3B8]'}
            data-testid="digital-view-list"
          >
            <ListIcon className="w-4 h-4 mr-2" /> List
          </Button>
          <Button
            variant={view === 'unscheduled' ? 'default' : 'outline'}
            onClick={() => setView('unscheduled')}
            className={view === 'unscheduled' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'border-amber-800/50 text-amber-300 hover:text-amber-200'}
            data-testid="digital-view-unscheduled"
          >
            <AlertCircle className="w-4 h-4 mr-2" /> Missing dates ({unscheduled.length})
          </Button>
        </div>
      </div>

      {/* Today panel */}
      {view !== 'unscheduled' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="digital-today-panel">
          <TodayCard title="Running today" items={runningToday} onClick={openBookingModal} productColor={productColor} productIconMap={productIconMap} testid="today-running-card" />
          <TodayCard title="Upcoming (next 7 days)" items={upcomingWeek} onClick={openBookingModal} productColor={productColor} productIconMap={productIconMap} testid="today-upcoming-card" />
          <TodayCard title="Ending soon (next 3 days)" items={endingSoon} onClick={openBookingModal} productColor={productColor} productIconMap={productIconMap} testid="today-endingsoon-card" />
        </div>
      )}

      {/* Filters */}
      <div className="p-3 bg-[#121212] border border-[#1E293B] rounded-md flex flex-wrap gap-2 items-center">
        <Filter className="w-4 h-4 text-[#94A3B8]" />
        <FilterSelect testid="filter-product-type" value={filters.productType} onValueChange={(v) => setFilters(f => ({ ...f, productType: v }))} placeholder="Product">
          <SelectItem value="all" className="text-[#F8FAFC]">All products</SelectItem>
          {productTypes.map(pt => (
            <SelectItem key={pt.id} value={pt.name} className="text-[#F8FAFC]">{pt.name}</SelectItem>
          ))}
        </FilterSelect>
        <FilterSelect testid="filter-advertiser" value={filters.advertiser} onValueChange={(v) => setFilters(f => ({ ...f, advertiser: v }))} placeholder="Advertiser">
          <SelectItem value="all" className="text-[#F8FAFC]">All advertisers</SelectItem>
          {advertisers.map(a => (
            <SelectItem key={a.id} value={a.id} className="text-[#F8FAFC]">{a.business_name}</SelectItem>
          ))}
        </FilterSelect>
        <FilterSelect testid="filter-sales-rep" value={filters.salesRep} onValueChange={(v) => setFilters(f => ({ ...f, salesRep: v }))} placeholder="Sales rep">
          <SelectItem value="all" className="text-[#F8FAFC]">All sales reps</SelectItem>
          {salesReps.map(r => (
            <SelectItem key={r.id} value={r.id} className="text-[#F8FAFC]">{r.name}</SelectItem>
          ))}
        </FilterSelect>
        <FilterSelect testid="filter-mode" value={filters.mode} onValueChange={(v) => setFilters(f => ({ ...f, mode: v }))} placeholder="Mode">
          <SelectItem value="all" className="text-[#F8FAFC]">Any duration</SelectItem>
          <SelectItem value="single" className="text-[#F8FAFC]">Single-day only</SelectItem>
          <SelectItem value="range" className="text-[#F8FAFC]">Date-range only</SelectItem>
        </FilterSelect>
        <FilterSelect testid="filter-status" value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))} placeholder="Status">
          <SelectItem value="all" className="text-[#F8FAFC]">Any status</SelectItem>
          <SelectItem value="confirmed" className="text-[#F8FAFC]">Confirmed</SelectItem>
          <SelectItem value="production" className="text-[#F8FAFC]">Production</SelectItem>
          <SelectItem value="invoiced" className="text-[#F8FAFC]">Invoiced</SelectItem>
          <SelectItem value="paid" className="text-[#F8FAFC]">Paid</SelectItem>
          <SelectItem value="completed" className="text-[#F8FAFC]">Completed</SelectItem>
          <SelectItem value="cancelled" className="text-[#F8FAFC]">Cancelled</SelectItem>
        </FilterSelect>
      </div>

      {/* Calendar */}
      {view === 'calendar' && (
        <div className="space-y-3" data-testid="digital-calendar-container">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={goPrev} className="text-[#94A3B8]" data-testid="digital-calendar-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToday} className="border-[#1E293B] text-[#94A3B8]" data-testid="digital-calendar-today">
                Today
              </Button>
              <Button variant="ghost" size="sm" onClick={goNext} className="text-[#94A3B8]" data-testid="digital-calendar-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <span className="ml-3 text-[#F8FAFC] font-medium" data-testid="digital-calendar-title">{rangeLabel}</span>
            </div>
            <div className="flex gap-1">
              {['month', 'week', 'day'].map(s => (
                <Button
                  key={s}
                  variant={calendarScope === s ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCalendarScope(s)}
                  className={calendarScope === s ? 'bg-[#3B82F6] hover:bg-[#2563EB] text-white' : 'border-[#1E293B] text-[#94A3B8]'}
                  data-testid={`digital-scope-${s}`}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </Button>
              ))}
            </div>
          </div>

          {calendarScope === 'month' && (
            <MonthGrid cursor={cursor} rangeBookings={rangeBookings} singleByDate={singleByDate} onItemClick={openBookingModal} productColor={productColor} productIconMap={productIconMap} />
          )}
          {calendarScope === 'week' && (
            <WeekView cursor={cursor} rangeBookings={rangeBookings} singleByDate={singleByDate} onItemClick={openBookingModal} productColor={productColor} productIconMap={productIconMap} />
          )}
          {calendarScope === 'day' && (
            <DayView cursor={cursor} rangeBookings={rangeBookings} singleByDate={singleByDate} onItemClick={openBookingModal} productColor={productColor} productIconMap={productIconMap} />
          )}
        </div>
      )}

      {/* List */}
      {view === 'list' && (
        <ScheduleList items={filtered} onItemClick={openBookingModal} productColor={productColor} productIconMap={productIconMap} testid="digital-list" />
      )}

      {/* Unscheduled */}
      {view === 'unscheduled' && (
        <div className="space-y-2" data-testid="digital-unscheduled-list">
          <div className="p-3 bg-amber-900/20 border border-amber-800 rounded-md text-amber-300 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {filteredUnscheduled.length} website booking(s) are missing schedule dates.
          </div>
          <ScheduleList items={filteredUnscheduled} onItemClick={openBookingModal} productColor={productColor} productIconMap={productIconMap} showMissingWarning />
        </div>
      )}

      {loading && <div className="text-[#94A3B8] text-sm">Loading digital schedule…</div>}

      {/* Edit schedule modal — inline lightweight form */}
      <ScheduleEditModal
        open={scheduleModalOpen}
        onOpenChange={setScheduleModalOpen}
        booking={selectedBooking}
        productTypes={productTypes}
        canEdit={canEdit}
        onSaved={() => { setScheduleModalOpen(false); loadSchedule(); }}
      />
    </div>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const FilterSelect = ({ value, onValueChange, placeholder, children, testid }) => (
  <div className="min-w-[160px]">
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-8 text-xs"
        data-testid={testid}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-[#121212] border-[#1E293B] max-h-[300px]">
        {children}
      </SelectContent>
    </Select>
  </div>
);

const BookingChip = ({ b, onClick, productColor, productIconMap, compact }) => {
  const color = b.product_type_calendar_color || productColor[b.product_type] || DEFAULT_COLOR;
  const Icon = iconFor(b, productIconMap);
  return (
    <button
      type="button"
      onClick={() => onClick(b)}
      className="w-full text-left rounded px-1.5 py-0.5 text-[10px] leading-tight truncate hover:brightness-125 transition"
      style={{ backgroundColor: `${color}33`, borderLeft: `3px solid ${color}` }}
      title={`${b.advertiser_name} — ${b.product_type}`}
      data-testid={`digital-booking-chip-${b.id}`}
    >
      <span className="flex items-center gap-1 truncate">
        <Icon className="w-3 h-3 flex-shrink-0" style={{ color }} />
        <span className={`truncate ${compact ? '' : 'font-medium'} text-[#F8FAFC]`}>{b.advertiser_name}</span>
      </span>
      {!compact && (
        <span className="block text-[9px] text-[#94A3B8] truncate">{b.product_type}</span>
      )}
    </button>
  );
};

const TodayCard = ({ title, items, onClick, productColor, productIconMap, testid }) => (
  <div className="p-3 bg-[#121212] border border-[#1E293B] rounded-md" data-testid={testid}>
    <p className="text-xs uppercase tracking-wide text-[#94A3B8] mb-2">{title}</p>
    <p className="text-2xl font-semibold text-[#F8FAFC]">{items.length}</p>
    <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
      {items.slice(0, 6).map(b => (
        <BookingChip key={b.id} b={b} onClick={onClick} productColor={productColor} productIconMap={productIconMap} compact />
      ))}
      {items.length === 0 && <p className="text-[11px] text-[#475569]">Nothing here yet.</p>}
    </div>
  </div>
);

// ─── Range Bar: continuous horizontal band that spans multiple columns ──────
const RangeBar = ({ segment, onClick, productColor, productIconMap }) => {
  const { booking, colStart, colEnd, startsInRow, endsInRow } = segment;
  const color = booking.product_type_calendar_color || productColor[booking.product_type] || DEFAULT_COLOR;
  const span = colEnd - colStart + 1;
  const Icon = iconFor(booking, productIconMap);
  return (
    <button
      type="button"
      onClick={() => onClick(booking)}
      className="absolute inset-y-0 rounded-md text-left px-2 truncate hover:brightness-125 transition flex items-center gap-1.5 text-[11px] leading-none"
      style={{
        left: `calc(${(colStart / 7) * 100}% + 3px)`,
        width: `calc(${(span / 7) * 100}% - 6px)`,
        backgroundColor: `${color}33`,
        borderLeft: startsInRow ? `3px solid ${color}` : `3px dashed ${color}`,
        borderRight: endsInRow ? `1px solid ${color}77` : 'none',
        borderTopRightRadius: endsInRow ? undefined : 0,
        borderBottomRightRadius: endsInRow ? undefined : 0,
        borderTopLeftRadius: startsInRow ? undefined : 0,
        borderBottomLeftRadius: startsInRow ? undefined : 0,
      }}
      title={`${booking.advertiser_name} — ${booking.product_type} (${booking.digital_start_date} → ${booking.digital_end_date})`}
      data-testid={`digital-range-bar-${booking.id}`}
    >
      <Icon className="w-3 h-3 flex-shrink-0" style={{ color }} />
      <span className="truncate font-medium text-[#F8FAFC]">{booking.advertiser_name}</span>
      <span className="truncate text-[#94A3B8] hidden sm:inline">· {booking.product_type}</span>
    </button>
  );
};

// A row of range bars (one horizontal band per lane), laid out by
// layoutRangeBookingsForWeek. Reserves vertical height = laneCount × BAR_H.
const BAR_HEIGHT_PX = 20; // matches h-5

const RangeBarsLayer = ({ segments, laneCount, onItemClick, productColor, productIconMap }) => {
  if (laneCount === 0) return null;
  return (
    <div
      className="relative w-full"
      style={{ height: laneCount * (BAR_HEIGHT_PX + 3) + 2 }}
      data-testid="digital-range-bars-layer"
    >
      {segments.map((seg) => (
        <div
          key={seg.booking.id}
          className="absolute left-0 right-0"
          style={{ top: seg.lane * (BAR_HEIGHT_PX + 3) + 1, height: BAR_HEIGHT_PX }}
        >
          <RangeBar segment={seg} onClick={onItemClick} productColor={productColor} productIconMap={productIconMap} />
        </div>
      ))}
    </div>
  );
};

const MonthGrid = ({ cursor, rangeBookings, singleByDate, onItemClick, productColor, productIconMap }) => {
  const first = startOfMonth(cursor);
  const gridStart = startOfWeek(first);
  const weeks = Array.from({ length: 6 }, (_, w) =>
    Array.from({ length: 7 }, (_, i) => addDays(gridStart, w * 7 + i)),
  );
  const inMonth = (d) => d.getMonth() === cursor.getMonth();
  const isToday = (d) => isoDate(d) === isoDate(new Date());

  return (
    <div className="rounded-md border border-[#1E293B] overflow-hidden bg-[#0A0A0A]" data-testid="digital-month-grid">
      <div className="grid grid-cols-7 border-b border-[#1E293B]">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="p-2 text-[11px] uppercase tracking-wide text-[#94A3B8] text-center border-r border-[#1E293B] last:border-r-0">{d}</div>
        ))}
      </div>
      {weeks.map((days, wIdx) => {
        const weekIsoDates = days.map(isoDate);
        const { segments, laneCount } = layoutRangeBookingsForWeek(weekIsoDates, rangeBookings);
        const weekKey = weekIsoDates[0];
        return (
          <div key={weekKey} className="border-b border-[#1E293B] last:border-b-0" data-testid={`digital-week-row-${wIdx}`}>
            {/* Day headers (date numbers) */}
            <div className="grid grid-cols-7">
              {days.map((d) => {
                const iso = isoDate(d);
                return (
                  <div
                    key={iso}
                    className={`px-1.5 pt-1.5 border-r border-[#1E293B] last:border-r-0 ${inMonth(d) ? 'bg-[#0A0A0A]' : 'bg-[#050505] opacity-60'}`}
                  >
                    <div className={`text-[11px] ${isToday(d) ? 'text-[#3B82F6] font-semibold' : inMonth(d) ? 'text-[#94A3B8]' : 'text-[#475569]'}`}>
                      {d.getDate()}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Range bars — one continuous bar per booking that touches this row */}
            <RangeBarsLayer segments={segments} laneCount={laneCount} onItemClick={onItemClick} productColor={productColor} productIconMap={productIconMap} />
            {/* Single-day items — per day cell, below the bars */}
            <div className="grid grid-cols-7 min-h-[60px]">
              {days.map((d) => {
                const iso = isoDate(d);
                const singles = singleByDate[iso] || [];
                return (
                  <div
                    key={iso}
                    className={`p-1.5 border-r border-[#1E293B] last:border-r-0 ${inMonth(d) ? 'bg-[#0A0A0A]' : 'bg-[#050505] opacity-60'}`}
                    data-testid={`digital-day-cell-${iso}`}
                  >
                    <div className="space-y-0.5">
                      {singles.slice(0, 3).map((b) => (
                        <BookingChip key={b.id} b={b} onClick={onItemClick} productColor={productColor} productIconMap={productIconMap} compact />
                      ))}
                      {singles.length > 3 && (
                        <p className="text-[9px] text-[#475569] pl-1">+{singles.length - 3} more</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const WeekView = ({ cursor, rangeBookings, singleByDate, onItemClick, productColor, productIconMap }) => {
  const start = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const weekIsoDates = days.map(isoDate);
  const { segments, laneCount } = layoutRangeBookingsForWeek(weekIsoDates, rangeBookings);
  const todayIso = isoDate(new Date());
  return (
    <div className="rounded-md border border-[#1E293B] overflow-hidden bg-[#0A0A0A]" data-testid="digital-week-grid">
      <div className="grid grid-cols-7">
        {days.map((d) => (
          <div key={isoDate(d)} className="p-2 border-r border-[#1E293B] last:border-r-0">
            <div className={`text-xs ${isoDate(d) === todayIso ? 'text-[#3B82F6] font-semibold' : 'text-[#94A3B8]'}`}>
              {shortDay(d)} {d.getDate()}
            </div>
          </div>
        ))}
      </div>
      {/* Range bars laid across the whole week */}
      <RangeBarsLayer segments={segments} laneCount={laneCount} onItemClick={onItemClick} productColor={productColor} productIconMap={productIconMap} />
      {/* Single-day items per day */}
      <div className="grid grid-cols-7 min-h-[200px]">
        {days.map((d) => {
          const iso = isoDate(d);
          const singles = singleByDate[iso] || [];
          return (
            <div key={iso} className="p-2 border-r border-[#1E293B] last:border-r-0 space-y-1" data-testid={`digital-week-day-${iso}`}>
              {singles.map((b) => (
                <BookingChip key={b.id} b={b} onClick={onItemClick} productColor={productColor} productIconMap={productIconMap} />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DayView = ({ cursor, rangeBookings, singleByDate, onItemClick, productColor, productIconMap }) => {
  const iso = isoDate(cursor);
  // Range bookings whose window includes this day
  const rangesToday = rangeBookings
    .filter((b) => {
      const s = b.digital_start_date;
      const e = b.digital_end_date || b.digital_start_date;
      return s && e && s <= iso && e >= iso;
    })
    .sort(sortRangeBookings);
  const singles = (singleByDate[iso] || []).slice().sort(sortSingleDayBookings);
  const total = rangesToday.length + singles.length;
  return (
    <div className="rounded-md border border-[#1E293B] bg-[#0A0A0A] p-4 min-h-[300px]" data-testid="digital-day-view">
      <p className="text-sm text-[#94A3B8] mb-3">{dayLabel(cursor)} — {total} booking(s)</p>
      {rangesToday.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">Running campaigns</p>
          <div className="space-y-1">
            {rangesToday.map((b) => (
              <RunningCampaignRow key={b.id} b={b} onClick={onItemClick} productColor={productColor} productIconMap={productIconMap} />
            ))}
          </div>
        </div>
      )}
      {singles.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">Single-day</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {singles.map((b) => (
              <BookingChip key={b.id} b={b} onClick={onItemClick} productColor={productColor} productIconMap={productIconMap} />
            ))}
          </div>
        </div>
      )}
      {total === 0 && (
        <p className="text-[#475569] text-sm">No digital bookings on this day.</p>
      )}
    </div>
  );
};

// Full-width row used in DayView to represent a running range campaign.
const RunningCampaignRow = ({ b, onClick, productColor, productIconMap }) => {
  const color = b.product_type_calendar_color || productColor[b.product_type] || DEFAULT_COLOR;
  const Icon = iconFor(b, productIconMap);
  return (
    <button
      type="button"
      onClick={() => onClick(b)}
      className="w-full text-left rounded-md px-3 py-1.5 text-xs flex items-center gap-2 hover:brightness-125 transition"
      style={{ backgroundColor: `${color}33`, borderLeft: `3px solid ${color}` }}
      data-testid={`digital-day-range-row-${b.id}`}
    >
      <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} />
      <span className="font-medium text-[#F8FAFC] truncate">{b.advertiser_name}</span>
      <span className="text-[#94A3B8] truncate">· {b.product_type}</span>
      <span className="ml-auto text-[#94A3B8] text-[10px] flex-shrink-0">
        {b.digital_start_date} → {b.digital_end_date}
      </span>
    </button>
  );
};

// When a booking was entered in the system (bookings.created_at, ISO string),
// shown in the viewer's local time as YYYY-MM-DD (or with the time for tooltips).
const formatCreatedAt = (iso, withTime = false) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const day = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return withTime ? `${day} ${pad(d.getHours())}:${pad(d.getMinutes())}` : day;
};

const ScheduleList = ({ items, onItemClick, productColor, productIconMap, showMissingWarning, testid }) => (
  <div className="rounded-md border border-[#1E293B] overflow-hidden" data-testid={testid || 'digital-schedule-list'}>
    <table className="w-full text-sm">
      <thead className="bg-[#121212] text-[#94A3B8] text-xs uppercase">
        <tr>
          {showMissingWarning && <th className="text-left px-3 py-2">Created</th>}
          <th className="text-left px-3 py-2">Dates</th>
          <th className="text-left px-3 py-2">Advertiser</th>
          <th className="text-left px-3 py-2">Product</th>
          <th className="text-left px-3 py-2">Status</th>
          <th className="text-left px-3 py-2">Rep</th>
          <th className="text-left px-3 py-2">Notes</th>
          <th className="text-right px-3 py-2">Actions</th>
        </tr>
      </thead>
      <tbody>
        {items.map(b => {
          const color = b.product_type_calendar_color || productColor[b.product_type] || DEFAULT_COLOR;
          const RowIcon = iconFor(b, productIconMap);
          return (
          <tr key={b.id} className="border-t border-[#1E293B] hover:bg-[#121212]/60">
            {showMissingWarning && (
              <td
                className="px-3 py-2 text-[#94A3B8] whitespace-nowrap"
                title={formatCreatedAt(b.created_at, true)}
                data-testid={`digital-list-created-${b.id}`}
              >
                {formatCreatedAt(b.created_at) || '—'}
              </td>
            )}
            <td className="px-3 py-2 text-[#F8FAFC]">
              {b.digital_start_date ? (
                b.digital_end_date && b.digital_end_date !== b.digital_start_date
                  ? <>{b.digital_start_date} → {b.digital_end_date}</>
                  : b.digital_start_date
              ) : (
                <span className="text-amber-400 text-xs">Not scheduled</span>
              )}
            </td>
            <td className="px-3 py-2 text-[#F8FAFC]">{b.advertiser_name}</td>
            <td className="px-3 py-2 text-[#94A3B8]">
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${color}33`, borderLeft: `2px solid ${color}` }}
                  data-testid={`digital-list-icon-${b.id}`}
                >
                  <RowIcon className="w-3 h-3" style={{ color }} />
                </span>
                {b.product_type}
              </span>
            </td>
            <td className="px-3 py-2 text-[#94A3B8]">
              <span className="inline-flex items-center gap-1.5">{b.status} <SentPill booking={b} /></span>
            </td>
            <td className="px-3 py-2 text-[#94A3B8] text-xs">{b.sales_rep_id ? '—' : ''}</td>
            <td className="px-3 py-2 text-[#94A3B8] text-xs truncate max-w-[200px]">{b.digital_schedule_notes || ''}</td>
            <td className="px-3 py-2 text-right">
              <Button size="sm" variant="outline" onClick={() => onItemClick(b)} className="border-[#1E293B] text-[#94A3B8] h-7 text-xs" data-testid={`digital-list-edit-${b.id}`}>
                {showMissingWarning ? 'Add dates' : 'Edit'}
              </Button>
            </td>
          </tr>
          );
        })}
        {items.length === 0 && (
          <tr>
            <td colSpan={showMissingWarning ? 8 : 7} className="px-3 py-6 text-center text-[#475569] text-sm">
              No bookings match the current filters.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

// ── Inline schedule editor modal — updates only the digital_* fields ──────
const ScheduleEditModal = ({ open, onOpenChange, booking, productTypes, canEdit, onSaved }) => {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // "Open full booking" jumps to the Bookings page with this booking's
  // complete form open. Hidden when the property has Bookings turned off.
  const navigate = useNavigate();
  const { on: featureOn } = useFeatures();

  useEffect(() => {
    if (booking) {
      setStart(booking.digital_start_date || '');
      setEnd(booking.digital_end_date || '');
      setNotes(booking.digital_schedule_notes || '');
      setError('');
    }
  }, [booking]);

  if (!booking) return null;
  const pt = productTypes.find(p => p.name === booking.product_type);
  const isSingle = !!pt?.single_day_product || !!booking.product_type_is_single_day;
  const iconKey = booking.product_type_calendar_icon || pt?.calendar_icon || null;
  const HeaderIcon = getCalendarIcon(iconKey);
  const color = booking.product_type_calendar_color || pt?.calendar_color || DEFAULT_COLOR;

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      if (!isSingle && start && end && end < start) {
        setError('End date cannot be before start date.');
        setSaving(false);
        return;
      }
      const payload = {
        digital_start_date: start || null,
        digital_end_date: isSingle ? (start || null) : (end || null),
        digital_schedule_notes: notes || null,
      };
      await api.put(`/digital/bookings/${booking.id}/schedule`, payload);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save schedule.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-md" data-testid="digital-schedule-modal">
        <DialogHeader>
          <DialogTitle>{canEdit ? 'Edit Schedule' : 'View Schedule'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-[#94A3B8] space-y-0.5">
            <p className="flex items-center gap-2">
              <span
                className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${color}33`, borderLeft: `2px solid ${color}` }}
                data-testid="digital-schedule-modal-icon"
              >
                <HeaderIcon className="w-3.5 h-3.5" style={{ color }} />
              </span>
              <span className="text-[#F8FAFC]">{booking.advertiser_name}</span>
            </p>
            <p>{booking.product_type} · {booking.status} · {isSingle ? 'Single-day' : 'Date-range'}</p>
          </div>
          <NewsdeskSentInfo booking={booking} />
          <SentControl booking={booking} canEdit={canEdit} onChanged={onSaved} />
          {isSingle ? (
            <div className="space-y-1">
              <Label className="text-[#94A3B8] text-xs">Scheduled date</Label>
              <Input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                disabled={!canEdit}
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                data-testid="digital-schedule-single-date"
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[#94A3B8] text-xs">Start</Label>
                <Input
                  type="date"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  disabled={!canEdit}
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                  data-testid="digital-schedule-start-date"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[#94A3B8] text-xs">End</Label>
                <Input
                  type="date"
                  value={end}
                  min={start || undefined}
                  onChange={(e) => setEnd(e.target.value)}
                  disabled={!canEdit}
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                  data-testid="digital-schedule-end-date"
                />
              </div>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-[#94A3B8] text-xs">Schedule notes</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!canEdit}
              placeholder="Campaign, targeting…"
              className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
              data-testid="digital-schedule-notes"
            />
          </div>
          {error && <p className="text-red-400 text-xs">{error}</p>}
          <div className="flex items-center justify-end gap-2 pt-2">
            {featureOn('bookings') && (
              <Button
                variant="outline"
                onClick={() => navigate(`/bookings?open=${encodeURIComponent(booking.id)}`)}
                className="border-[#1E293B] text-[#94A3B8] mr-auto"
                data-testid="digital-schedule-open-booking-btn"
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open full booking
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#1E293B] text-[#94A3B8]">
              Cancel
            </Button>
            {canEdit && (
              <Button onClick={save} disabled={saving} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="digital-schedule-save-btn">
                {saving ? 'Saving…' : 'Save'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DigitalPage;
